import { Component, EventEmitter, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TemplateManagementService } from '../../../../services/templates/template.management.service';
import { TemplateAssignmentType } from '../../../../models/templates/template.assignment.type';

@Component({
  selector: 'app-acknowledgement-settings',
  templateUrl: './acknowledgement-settings.component.html',
  styleUrls: ['./acknowledgement-settings.component.scss', '../tool-settings.scss']
})
export class AcknowledgementSettingsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public tms: TemplateManagementService;
  @Input() strictReadOnly: boolean = false;
  @Input() ackDefaultTemplate: boolean = false;
  @Input() ackEmailSubject: string = '';
  @Input() ackSenderEmail: string = '';
  @Input() ackSenderName: string = '';

  @Output() formChange = new EventEmitter<any>();

  private lastValidEmail: string = '';

  public ackForm: FormGroup = this.fb.group({
    isDefault: [false],
    emailSubject: [''],
    senderEmail: ['', [Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
    senderName: ['']
  });

  private subscription: Subscription = new Subscription();

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.updateFormValues();
    this.setupFormSubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.ackDefaultTemplate || changes.ackEmailSubject || 
        changes.ackSenderEmail || changes.ackSenderName) {
      this.updateFormValues();
    }

    // Update email subject validation based on template type and isDefault
    if (changes.tms || changes.ackEmailSubject) {
      const isEmailSubjectRequired = this.tms && 
        (this.tms.templateAssignmentType === TemplateAssignmentType.Campaign || !this.tms.isLetterBuilder) &&
        this.ackForm.get('isDefault').value;
      
      const emailSubjectControl = this.ackForm.get('emailSubject');
      if (isEmailSubjectRequired) {
        emailSubjectControl.setValidators([Validators.required]);
      } else {
        emailSubjectControl.clearValidators();
      }
      emailSubjectControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private setupFormSubscription(): void {
    this.subscription.add(
      this.ackForm.valueChanges
        .pipe(
          debounceTime(0),
          distinctUntilChanged()
        )
        .subscribe(value => {
          // Update email subject validation when isDefault changes
          const emailSubjectControl = this.ackForm.get('emailSubject');
          const isEmailSubjectRequired = this.tms && 
            (this.tms.templateAssignmentType === TemplateAssignmentType.Campaign || !this.tms.isLetterBuilder) &&
            value.isDefault;
          
          if (isEmailSubjectRequired) {
            emailSubjectControl.setValidators([Validators.required]);
          } else {
            emailSubjectControl.clearValidators();
          }
          emailSubjectControl.updateValueAndValidity({ emitEvent: false });

          // Only emit if the form is valid or if senderEmail is empty
          if (this.ackForm.valid || (!value.senderEmail && !this.ackForm.get('senderEmail').touched)) {
            this.formChange.emit({
              ackDefaultTemplate: value.isDefault,
              ackEmailSubject: value.emailSubject,
              ackSenderEmail: value.senderEmail,
              ackSenderName: value.senderName
            });
          }
        })
    );

    // Add specific subscription for email changes
    this.subscription.add(
      this.ackForm.get('senderEmail').valueChanges
        .subscribe(value => {
          const emailControl = this.ackForm.get('senderEmail');
          if (emailControl.valid) {
            this.lastValidEmail = value;
          }
        })
    );
  }

  private updateFormValues(): void {
    const formValues = {
      isDefault: this.ackDefaultTemplate || false,
      emailSubject: this.ackEmailSubject || '',
      senderEmail: this.ackSenderEmail || '',
      senderName: this.ackSenderName || ''
    };

    // Update lastValidEmail when setting initial values
    if (this.ackSenderEmail && this.ackForm.get('senderEmail').valid) {
      this.lastValidEmail = this.ackSenderEmail;
    }

    this.ackForm.patchValue(formValues, { emitEvent: false });
  }

  public markAllAsTouched(): void {
    this.ackForm.markAllAsTouched();
  }

  public get isEmailSubjectRequired(): boolean {
    return this.tms && 
           (this.tms.templateAssignmentType === TemplateAssignmentType.Campaign || !this.tms.isLetterBuilder) &&
           this.ackForm.get('isDefault').value;
  }
}
